import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Search, Download, Mail, Eye, Users, MapPin, BarChart3, Building2, Loader2, PlayCircle, ArrowUp, ArrowDown, ArrowUpDown, Trash2, Send, Undo2, Shield, Phone, Columns3, Check } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import * as XLSX from "xlsx";
import { MailingRecordDetail } from "./MailingRecordDetail";
import { BusinessLookupDialog } from "./BusinessLookupDialog";
import { BusinessEntityReview } from "./BusinessEntityReview";
import { ReverseCommitDialog } from "./ReverseCommitDialog";
import { TrusteeReviewDialog } from "./TrusteeReviewDialog";

const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];


interface MailingRecord {
  id: string;
  seller_id: string;
  county: string | null;
  origin_state: string | null;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  consideration: number | null;
  document_type: string | null;
  property_address: string | null;
  response: string | null;
  phone_number: string | null;
  email_address: string | null;
  mailing_status: string;
  times_mailed: number;
  last_mailed_date: string | null;
  recorded_date: string | null;
  tm_notes: string | null;
  tm_dropdown: string | null;
  date_of_birth: string | null;
  household_income: string | null;
  credit_card_user: string | null;
  ethnicity_flag: string | null;
}

interface ColumnDef {
  key: string;
  label: string;
  defaultVisible: boolean;
  editable?: boolean;
  editType?: "text" | "number";
  dbField?: string; // maps to DB column, defaults to key
}

const ALL_TABLE_COLUMNS: ColumnDef[] = [
  { key: "name", label: "Name", defaultVisible: true, editable: false },
  { key: "first_name", label: "First Name", defaultVisible: false, editable: true },
  { key: "last_name", label: "Last Name", defaultVisible: false, editable: true },
  { key: "business_name", label: "Business", defaultVisible: false, editable: true },
  { key: "county", label: "County", defaultVisible: true, editable: true },
  { key: "origin_state", label: "State", defaultVisible: true, editable: true },
  { key: "document_type", label: "Doc Type", defaultVisible: true, editable: true },
  { key: "recorded_date", label: "Recorded", defaultVisible: true, editable: true },
  { key: "consideration", label: "Amount", defaultVisible: true, editable: true, editType: "number" },
  { key: "times_mailed", label: "Mailed", defaultVisible: true, editable: false },
  { key: "last_mailed_date", label: "Last Mailed", defaultVisible: true, editable: false },
  { key: "mailing_status", label: "Status", defaultVisible: true, editable: false },
  { key: "response", label: "Response", defaultVisible: false, editable: true },
  { key: "street_address", label: "Street", defaultVisible: false, editable: true },
  { key: "city", label: "City", defaultVisible: false, editable: true },
  { key: "state", label: "Mail St", defaultVisible: false, editable: true },
  { key: "zip_code", label: "Zip", defaultVisible: false, editable: true },
  { key: "property_address", label: "Property Addr", defaultVisible: false, editable: true },
  { key: "pid", label: "PID", defaultVisible: false, editable: true },
  { key: "seller_id", label: "Seller ID", defaultVisible: false, editable: false },
  { key: "prefix", label: "Prefix", defaultVisible: false, editable: true },
  { key: "phone_number", label: "Phone", defaultVisible: false, editable: true },
  { key: "email_address", label: "Email", defaultVisible: false, editable: true },
  { key: "household_income", label: "Income", defaultVisible: false, editable: true },
  { key: "tm_notes", label: "TM Notes", defaultVisible: false, editable: true },
  { key: "tm_dropdown", label: "TM Status", defaultVisible: false, editable: false },
  { key: "ethnicity_flag", label: "Ethnicity", defaultVisible: false, editable: true },
];

const VISIBLE_COLUMNS_KEY = "mailing_table_visible_columns";

function getInitialVisibleColumns(): Set<string> {
  try {
    const saved = localStorage.getItem(VISIBLE_COLUMNS_KEY);
    if (saved) return new Set(JSON.parse(saved));
  } catch {}
  return new Set(ALL_TABLE_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
}

export function MailingRecordsTable({ refreshKey }: { refreshKey: number }) {
  const [records, setRecords] = useState<MailingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [countyFilter, setCountyFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [docTypeFilter, setDocTypeFilter] = useState("all");
  const [mailedFilter, setMailedFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [responseFilter, setResponseFilter] = useState("all");
  const [phoneFilter, setPhoneFilter] = useState("all");
  const [calledFilter, setCalledFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [ethnicityFilter, setEthnicityFilter] = useState("all");
  const [recordedBefore, setRecordedBefore] = useState("");
  const [recordedAfter, setRecordedAfter] = useState("");
  const [lastMailedBefore, setLastMailedBefore] = useState("");
  const [lastMailedAfter, setLastMailedAfter] = useState("");
  const [counties, setCounties] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [docTypes, setDocTypes] = useState<string[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<MailingRecord | null>(null);
  const [lookupRecord, setLookupRecord] = useState<MailingRecord | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>("last_name");
  const [sortAsc, setSortAsc] = useState(true);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, current: '' });
  const [showBusinessReview, setShowBusinessReview] = useState(false);
  const [commitCampaign, setCommitCampaign] = useState("");
  const [commitDate, setCommitDate] = useState(new Date().toISOString().split("T")[0]);
  const [commitRunning, setCommitRunning] = useState(false);
  const [commitLimit, setCommitLimit] = useState<number | null>(null);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [showReverseDialog, setShowReverseDialog] = useState(false);
  const [showTrusteeReview, setShowTrusteeReview] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [campaignNames, setCampaignNames] = useState<string[]>([]);
  const [campaignRecordIds, setCampaignRecordIds] = useState<string[] | null>(null);
  const [letterRoundFilter, setLetterRoundFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState<"all" | "out_of_state" | "in_state">("all");
  const [locationRecordIds, setLocationRecordIds] = useState<string[] | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(getInitialVisibleColumns);
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const PAGE_SIZE = 100;

  const normalizeCountyLabel = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const applyCountyFilter = (query: any) => {
    if (countyFilter === "all") return query;
    return query.ilike("county", countyFilter);
  };

  // Stats
  const [stats, setStats] = useState({ total: 0, responded: 0, unmailed: 0 });

  // Debounce search input - wait 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchFilterOptions();
    fetchStats();
    fetchCampaignNames();
  }, [refreshKey]);

  useEffect(() => {
    fetchCampaignRecordIds();
  }, [campaignFilter, letterRoundFilter]);

  useEffect(() => {
    fetchLocationRecordIds();
  }, [locationFilter]);

  useEffect(() => {
    fetchRecords();
  }, [debouncedSearch, countyFilter, stateFilter, statusFilter, docTypeFilter, mailedFilter, entityFilter, responseFilter, phoneFilter, calledFilter, ageFilter, ethnicityFilter, recordedBefore, recordedAfter, lastMailedBefore, lastMailedAfter, page, sortColumn, sortAsc, refreshKey, campaignRecordIds, locationRecordIds]);

  const fetchFilterOptions = async () => {
    // Fetch ALL records in batches to get complete distinct values
    const allCounties = new Map<string, string>();
    const allStates = new Set<string>();
    const allDocTypes = new Set<string>();
    let page = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const { data } = await supabase
        .from("mailing_records")
        .select("county, origin_state, document_type")
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (!data || data.length === 0) {
        hasMore = false;
      } else {
        data.forEach((r: any) => {
          if (r.county) {
            const normalizedCounty = normalizeCountyLabel(r.county);
            allCounties.set(normalizedCounty.toUpperCase(), normalizedCounty);
          }
          if (r.origin_state) allStates.add(r.origin_state);
          if (r.document_type) allDocTypes.add(r.document_type);
        });
        hasMore = data.length === pageSize;
        page++;
      }
    }

    setCounties(Array.from(allCounties.values()).sort((a, b) => a.localeCompare(b)));
    setStates([...allStates].sort());
    setDocTypes([...allDocTypes].sort());
  };

  const fetchCampaignNames = async () => {
    let allNames = new Set<string>();
    let pg = 0;
    const pgSize = 1000;
    let hasMore = true;
    while (hasMore) {
      const { data } = await supabase
        .from("mailing_history")
        .select("campaign_name")
        .not("campaign_name", "is", null)
        .range(pg * pgSize, (pg + 1) * pgSize - 1);
      if (!data || data.length === 0) { hasMore = false; break; }
      data.forEach((r: any) => { if (r.campaign_name) allNames.add(r.campaign_name); });
      hasMore = data.length === pgSize;
      pg++;
    }
    setCampaignNames([...allNames].sort());
  };

  const fetchCampaignRecordIds = async () => {
    if (campaignFilter === "all" && letterRoundFilter === "all") { 
      setCampaignRecordIds(null); 
      return; 
    }
    const rpcParams: any = {};
    if (campaignFilter !== "all") rpcParams.p_campaign = campaignFilter;
    if (letterRoundFilter !== "all") rpcParams.p_mailing_round = parseInt(letterRoundFilter);
    
    const { data, error } = await (supabase as any).rpc('search_mailing_by_campaign', rpcParams);
    if (error) {
      console.error("Campaign/round filter error:", error);
      toast.error("Failed to load filtered records");
      setCampaignRecordIds([]);
      return;
    }
    setCampaignRecordIds(data || []);
  };

  const fetchLocationRecordIds = async () => {
    if (locationFilter === "all") {
      setLocationRecordIds(null);
      return;
    }
    const { data, error } = await (supabase as any).rpc('search_mailing_by_owner_location', { p_location: locationFilter });
    if (error) {
      console.error("Location filter error:", error);
      toast.error("Failed to load out-of-state filter");
      setLocationRecordIds([]);
      return;
    }
    setLocationRecordIds(data || []);
  };

  const fetchStats = async () => {
    const { count: total } = await supabase.from("mailing_records").select("*", { count: "exact", head: true });
    const { count: responded } = await supabase.from("mailing_records").select("*", { count: "exact", head: true }).not("response", "is", null).neq("response", "");
    const { count: unmailed } = await supabase.from("mailing_records").select("*", { count: "exact", head: true }).eq("times_mailed", 0);
    setStats({ total: total || 0, responded: responded || 0, unmailed: unmailed || 0 });
  };

  const applySearchFilter = (query: any, searchValue: string) => {
    if (!searchValue) return query;
    const words = searchValue.trim().split(/\s+/);
    const searchFields = ['last_name', 'first_name', 'seller_id', 'city', 'property_address', 'street_address'];
    for (const word of words) {
      query = query.or(searchFields.map(f => `${f}.ilike.%${word}%`).join(','));
    }
    return query;
  };

  // Shared filter-builder: applies ALL active filters to a query (single source of truth)
  const applyAllFilters = (query: any) => {
    query = applySearchFilter(query, debouncedSearch);
    query = applyCountyFilter(query);
    if (stateFilter.length > 0) {
      // Filter by origin_state (the property/recording state), not the mailing address state.
      query = query.in("origin_state", stateFilter);
    }
    if (statusFilter !== "all") query = query.eq("mailing_status", statusFilter);
    if (docTypeFilter !== "all") query = query.eq("document_type", docTypeFilter);
    if (mailedFilter === "0") query = query.eq("times_mailed", 0);
    else if (mailedFilter === "1") query = query.eq("times_mailed", 1);
    else if (mailedFilter === "2") query = query.eq("times_mailed", 2);
    else if (mailedFilter === "2+") query = query.gte("times_mailed", 2);
    if (entityFilter === "business") query = query.not("business_name", "is", null).neq("business_name", "");
    else if (entityFilter === "individual") query = query.or("business_name.is.null,business_name.eq.");
    if (responseFilter === "none") query = query.or("response.is.null,response.eq.");
    else if (responseFilter === "has") query = query.not("response", "is", null).neq("response", "");
    if (phoneFilter === "has") query = query.not("phone_number", "is", null).neq("phone_number", "");
    else if (phoneFilter === "none") query = query.or("phone_number.is.null,phone_number.eq.");
    if (calledFilter === "called") query = query.not("tm_dropdown", "is", null).neq("tm_dropdown", "");
    else if (calledFilter === "not_called") query = query.or("tm_dropdown.is.null,tm_dropdown.eq.");
    if (recordedBefore) query = query.lte("recorded_date", recordedBefore);
    if (recordedAfter) query = query.gte("recorded_date", recordedAfter);
    if (lastMailedBefore) query = query.lte("last_mailed_date", lastMailedBefore);
    if (lastMailedAfter) query = query.gte("last_mailed_date", lastMailedAfter);
    if (ageFilter !== "all") {
      const minAge = parseInt(ageFilter);
      const cutoffYear = new Date().getFullYear() - minAge;
      const cutoff = `${cutoffYear}12`;
      query = query.not("date_of_birth", "is", null).neq("date_of_birth", "").lte("date_of_birth", cutoff);
    }
    if (ethnicityFilter === "hispanic") query = query.eq("ethnicity_flag", "hispanic");
    else if (ethnicityFilter === "none") query = query.is("ethnicity_flag", null);
    return query;
  };

  // Apply campaign ID filter with batching for large ID sets
  const applyIdFilter = async (baseQuery: any, ids: string[]) => {
    const BATCH_SIZE = 200;
    if (ids.length <= BATCH_SIZE) {
      return { query: baseQuery.in("id", ids), batched: false };
    }
    // Batch and merge for large sets
    let allData: any[] = [];
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const batch = ids.slice(i, i + BATCH_SIZE);
      let batchQuery = supabase.from("mailing_records").select("*");
      batchQuery = applyAllFilters(batchQuery);
      batchQuery = batchQuery.in("id", batch);
      const { data: batchData } = await batchQuery;
      if (batchData) allData = allData.concat(batchData);
    }
    return { data: allData, batched: true };
  };

  // Intersect ID-based filters (campaign/round + owner-location) into a single effective set.
  const getEffectiveIds = (): string[] | null => {
    if (campaignRecordIds === null && locationRecordIds === null) return null;
    if (campaignRecordIds !== null && locationRecordIds !== null) {
      const set = new Set(locationRecordIds);
      return campaignRecordIds.filter(id => set.has(id));
    }
    return (campaignRecordIds ?? locationRecordIds) as string[];
  };

  const fetchRecords = async () => {
    setLoading(true);
    let query = supabase.from("mailing_records").select("*", { count: "exact" });
    query = applyAllFilters(query);

    const effectiveIds = getEffectiveIds();
    if (effectiveIds !== null) {
      if (effectiveIds.length === 0) {
        setRecords([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }
      const result = await applyIdFilter(query, effectiveIds);
      if (result.batched) {
        const dbSortColumn = sortColumn === "name" ? "last_name" : sortColumn;
        result.data!.sort((a: any, b: any) => {
          const aVal = a[dbSortColumn] || "";
          const bVal = b[dbSortColumn] || "";
          return sortAsc ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
        });
        setTotalCount(result.data!.length);
        setRecords(result.data!.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE));
        setLoading(false);
        return;
      }
      query = result.query;
    }

    const dbSortColumn = sortColumn === "name" ? "last_name" : sortColumn;
    const { data, count, error } = await query
      .order(dbSortColumn, { ascending: sortAsc, nullsFirst: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (error) {
      toast.error("Failed to fetch records");
      console.error(error);
    } else {
      setRecords(data || []);
      setTotalCount(count || 0);
    }
    setLoading(false);
  };

  const buildFilteredQuery = () => {
    let query = supabase.from("mailing_records").select("*");
    query = applyAllFilters(query);
    const effectiveIds = getEffectiveIds();
    if (effectiveIds !== null) {
      if (effectiveIds.length === 0) return query.eq("id", "00000000-0000-0000-0000-000000000000");
      const limitedIds = effectiveIds.slice(0, 200);
      query = query.in("id", limitedIds);
    }
    return query;
  };

  const handleExport = async () => {
    let allRecords: any[] = [];
    let from = 0;
    const pageSize = 1000;
    while (true) {
      const query = buildFilteredQuery();
      const { data, error } = await query
        .order(sortColumn, { ascending: sortAsc, nullsFirst: false })
        .range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      allRecords = [...allRecords, ...data];
      if (data.length < pageSize) break;
      from += pageSize;
    }
    if (!allRecords.length) { toast.error("No records to export"); return; }
    const toProperCase = (s: string | null) => {
      if (!s) return "";
      return s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substring(1).toLowerCase());
    };
    const rows = allRecords.map((r) => {
      const lastNameRaw = r.last_name || "";
      const nameSuffix = lastNameRaw.match(/,\s*(Trustee|Successor Trustee)/i)?.[0] || r.suffix || "";
      const cleanLastName = lastNameRaw.replace(/,\s*(Trustee|Successor Trustee)/i, "").trim();
      return {
        "Seller ID": r.seller_id || "",
        "First Name": toProperCase(r.first_name),
        "Last Name": toProperCase(cleanLastName),
        "Name Suffix": nameSuffix.replace(/^,\s*/, ""),
        "Business Name": r.business_name || "",
        "Street Address": r.street_address || "",
        "City": toProperCase(r.city),
        "State": (r.state || "").toUpperCase(),
        "Zip Code": r.zip_code || "",
        "County": toProperCase(r.county),
        "Origin State": (r.origin_state || "").toUpperCase(),
        "Document Type": r.document_type || "",
        "Recorded Date": r.recorded_date || "",
        "Consideration": r.consideration || "",
        "Times Mailed": r.times_mailed,
        "Last Mailed": r.last_mailed_date || "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 16 }, { wch: 20 }, { wch: 24 }, { wch: 28 }, { wch: 16 },
      { wch: 6 }, { wch: 10 }, { wch: 14 }, { wch: 6 }, { wch: 18 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mailing Records");
    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Mailing_Export_${dateStr}.xlsx`);
    toast.success(`Exported ${rows.length} records to Excel`);
  };

  const handleMailExport = async () => {
    let allRecords: any[] = [];
    let from = 0;
    const pageSize = 1000;
    while (true) {
      const query = buildFilteredQuery();
      const { data, error } = await query
        .order(sortColumn, { ascending: sortAsc, nullsFirst: false })
        .range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      allRecords = [...allRecords, ...data];
      if (data.length < pageSize) break;
      from += pageSize;
    }
    if (!allRecords.length) { toast.error("No records to export"); return; }
    const toProperCase = (s: string | null) => {
      if (!s) return "";
      return s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substring(1).toLowerCase());
    };
    const rows = allRecords.map((r) => {
      const lastNameRaw = r.last_name || "";
      const nameSuffix = lastNameRaw.match(/,\s*(Trustee|Successor Trustee)/i)?.[0] || r.suffix || "";
      const cleanLastName = lastNameRaw.replace(/,\s*(Trustee|Successor Trustee)/i, "").trim();
      return {
        "Seller ID": r.seller_id || "",
        "First Name": toProperCase(r.first_name),
        "Last Name": toProperCase(cleanLastName),
        "Name Suffix": nameSuffix.replace(/^,\s*/, ""),
        "Street Address": r.street_address || "",
        "City": toProperCase(r.city),
        "State": (r.state || "").toUpperCase(),
        "Zip Code": r.zip_code || "",
        "County": toProperCase(r.county),
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 20 }, { wch: 18 }, { wch: 22 }, { wch: 14 }, { wch: 30 }, { wch: 18 },
      { wch: 8 }, { wch: 12 }, { wch: 16 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mail List");
    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Mail_List_${dateStr}.xlsx`);
    toast.success(`Mail export: ${rows.length} records`);
  };

  const handleCommitToMail = async () => {
    if (!commitCampaign.trim()) { toast.error("Enter a campaign/mailing name"); return; }
    if (!commitDate) { toast.error("Enter a mail date"); return; }

    setCommitRunning(true);
    try {
      // Fetch all filtered records with id and times_mailed
      let allRecords: { id: string; times_mailed: number }[] = [];
      let from = 0;
      const pageSize = 1000;
      while (true) {
        let query = supabase.from("mailing_records").select("id, times_mailed");
        query = applyAllFilters(query);
        if (campaignRecordIds !== null && campaignRecordIds.length > 0) {
          const batch = campaignRecordIds.slice(from, from + pageSize);
          if (batch.length === 0) break;
          query = query.in("id", batch.slice(0, 200));
        }
        const { data, error } = await query.range(from, from + pageSize - 1);
        if (error || !data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allRecords.length === 0) {
        toast.error("No records match current filters");
        setCommitRunning(false);
        return;
      }

      // Apply optional limit
      if (commitLimit && commitLimit > 0 && commitLimit < allRecords.length) {
        allRecords = allRecords.slice(0, commitLimit);
      }

      // Process in batches
      const batchSize = 500;
      let created = 0;
      for (let i = 0; i < allRecords.length; i += batchSize) {
        const batch = allRecords.slice(i, i + batchSize);

        // Create mailing_history entries
        const historyRows = batch.map(r => ({
          mailing_record_id: r.id,
          campaign_name: commitCampaign.trim(),
          mailed_date: commitDate,
          mailing_round: r.times_mailed + 1,
          letter_type: "first-class",
        }));

        const { error: histError } = await supabase.from("mailing_history").insert(historyRows);
        if (histError) { console.error(histError); continue; }

        // Update each record's times_mailed and last_mailed_date
        for (const rec of batch) {
          await supabase
            .from("mailing_records")
            .update({
              times_mailed: rec.times_mailed + 1,
              last_mailed_date: commitDate,
            })
            .eq("id", rec.id);
        }

        created += batch.length;
      }

      toast.success(`✅ Committed ${created} records as mailed — "${commitCampaign}" on ${commitDate}`);
      setShowCommitDialog(false);
      setCommitCampaign("");
      fetchRecords();
      fetchStats();
    } catch (err) {
      console.error(err);
      toast.error("Commit failed");
    } finally {
      setCommitRunning(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toTitleCase = (str: string) =>
    str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

  const handleApplyLookup = async (recordId: string, contactName: string) => {
    const parts = contactName.trim().split(/\s+/);
    const firstName = toTitleCase(parts[0] || '');
    const lastName = toTitleCase(parts.slice(1).join(' ') || '');

    const { error } = await supabase
      .from('mailing_records')
      .update({ first_name: firstName, last_name: lastName })
      .eq('id', recordId);

    if (error) {
      toast.error('Failed to update record');
    } else {
      toast.success(`Updated contact to ${contactName}`);
      fetchRecords();
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const { error } = await supabase.from('mailing_records').delete().in('id', ids);
    if (error) {
      toast.error('Failed to delete records');
    } else {
      toast.success(`Deleted ${ids.length} record(s)`);
      setSelectedIds(new Set());
      fetchRecords();
      fetchStats();
    }
  };

  const handleDeleteSingle = async (id: string) => {
    const { error } = await supabase.from('mailing_records').delete().eq('id', id);
    if (error) {
      toast.error('Failed to delete record');
    } else {
      toast.success('Record deleted');
      fetchRecords();
      fetchStats();
    }
  };

  const handleMarkAsBusiness = async (record: MailingRecord) => {
    const fullName = `${record.first_name || ''} ${record.last_name || ''}`.trim();
    if (!fullName) return;
    const { error } = await supabase
      .from('mailing_records')
      .update({ business_name: fullName, first_name: null, last_name: null })
      .eq('id', record.id);
    if (error) {
      toast.error('Failed to mark as business');
    } else {
      toast.success(`Marked "${fullName}" as business`);
      // Open lookup dialog immediately with the updated record
      const updatedRecord = { ...record, business_name: fullName, first_name: null, last_name: null };
      setLookupRecord(updatedRecord);
      fetchRecords();
    }
  };

  const selectedBusinessRecords = records.filter(r => selectedIds.has(r.id) && r.business_name);

  const handleBatchLookup = async () => {
    if (selectedBusinessRecords.length === 0) {
      toast.error('No business records selected');
      return;
    }

    setBatchRunning(true);
    const total = selectedBusinessRecords.length;
    setBatchProgress({ done: 0, total, current: '' });
    let successCount = 0;

    for (let i = 0; i < total; i++) {
      const r = selectedBusinessRecords[i];
      setBatchProgress({ done: i, total, current: r.business_name || '' });

      try {
        const { data, error: fnError } = await supabase.functions.invoke('lookup-business-entity', {
          body: { business_name: r.business_name, state: r.origin_state },
        });

        if (fnError || !data?.success || !data?.data?.contact_name) continue;

        const contactName = data.data.contact_name;
        const parts = contactName.trim().split(/\s+/);
        const firstName = toTitleCase(parts[0] || '');
        const lastName = toTitleCase(parts.slice(1).join(' ') || '');

        const { error: updateError } = await supabase
          .from('mailing_records')
          .update({ first_name: firstName, last_name: lastName })
          .eq('id', r.id);

        if (!updateError) successCount++;
      } catch {
        // skip failures silently
      }
    }

    setBatchProgress({ done: total, total, current: '' });
    setBatchRunning(false);
    setSelectedIds(new Set());
    toast.success(`Updated ${successCount} of ${total} records`);
    fetchRecords();
  };

  const toggleColumnVisibility = (key: string) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const HISPANIC_SURNAMES = new Set([
    'GARCIA','RODRIGUEZ','MARTINEZ','HERNANDEZ','LOPEZ','GONZALEZ','GONZALES',
    'PEREZ','SANCHEZ','RAMIREZ','TORRES','FLORES','RIVERA','GOMEZ','DIAZ',
    'REYES','MORALES','CRUZ','ORTIZ','GUTIERREZ','CHAVEZ','RAMOS','VARGAS',
    'CASTILLO','JIMENEZ','MORENO','ROMERO','HERRERA','MEDINA','AGUILAR',
    'GARZA','CASTRO','VASQUEZ','RUIZ','ALVAREZ','MENDOZA','GUZMAN','MUNOZ',
    'FERNANDEZ','DELGADO','SALAZAR','FUENTES','PENA','ESPINOZA','GUERRERO',
    'SILVA','CONTRERAS','SANDOVAL','LEON','VELASQUEZ','DOMINGUEZ','SOTO',
    'SANTIAGO','ESTRADA','ACOSTA','MALDONADO','VEGA','CAMPOS','MOLINA',
    'NUNEZ','AVILA','PADILLA','CERVANTES','ROJAS','CARDENAS','VALDEZ',
    'MARQUEZ','OCHOA','CORTEZ','CABRERA','RIOS','MEJIA','VILLARREAL',
    'TRUJILLO','CARRILLO','SERRANO','FIGUEROA','GALLEGOS','MONTOYA',
    'ROSALES','SOLIS','PACHECO','VILLANUEVA','DURAN','ALVARADO','MIRANDA',
    'LARA','RANGEL','BAUTISTA','ARELLANO','AYALA','ZAMORA','ZUNIGA',
    'OROZCO','PALACIOS','IBARRA','CISNEROS','LUGO','MERCADO','SALAS',
    'QUINTERO','MATA','ESCOBAR','TAPIA','MURILLO','VALENZUELA','NAVA',
    'NAVARRO','AGUIRRE','BARAJAS','MAYA','CORONADO','MELENDEZ','BARRERA',
    'JUAREZ','VILLEGAS','MARIN','CORDOVA','ARROYO','ESCALANTE','PANTOJA',
    'TOVAR','BERMUDEZ','QUINTANA','ESQUIVEL','SUAREZ','LEAL','PONCE',
    'SOLANO','ESPINOSA','CARDONA','VELAZQUEZ','SEPULVEDA','LUCERO',
    'MACIAS','ZARATE','ORNELAS','CAMACHO','COLON','CASILLAS','OLIVAS',
    'HUERTA','CALDERON','SOTELO','MONTES','RUBIO','JARAMILLO','BAEZ',
    'ROSARIO','BANDA','ZAPATA','SEGURA','CARRASCO','LEYVA','BALDERAS',
    'DELAROSA','MADRIGAL','CAZARES','OLIVARES','BELTRAN','RICO','VELA',
    'AREVALO','BECERRA','TELLEZ','URIBE','CARBAJAL','ALANIZ','YANEZ',
  ]);

  const HISPANIC_FIRST_NAMES = new Set([
    'JOSE','JOSÉ','MARIA','MARÍA','JUAN','CARLOS','LUIS','JORGE','MIGUEL','JESUS','JESÚS',
    'FRANCISCO','JAVIER','ANTONIO','MANUEL','PEDRO','RAFAEL','ROBERTO','RICARDO','FERNANDO',
    'ALEJANDRO','ALEJANDRA','ANDRES','ANDRÉS','ARMANDO','ARTURO','CESAR','CÉSAR','DANIEL',
    'DIEGO','EDGAR','EDUARDO','ELIAS','ELÍAS','ENRIQUE','ERNESTO','ESTEBAN','FELIPE','FELIX',
    'FÉLIX','GERARDO','GILBERTO','GUILLERMO','GUSTAVO','HECTOR','HÉCTOR','HERNAN','HERNÁN',
    'HORACIO','HUGO','HUMBERTO','IGNACIO','ISMAEL','ISRAEL','IVAN','IVÁN','JAIME','JAIRO',
    'JOAQUIN','JOAQUÍN','JULIO','LEONARDO','LEONEL','LORENZO','MARCO','MARCOS','MARIO',
    'MARTIN','MARTÍN','MAURICIO','MAXIMO','MOISES','MOISÉS','NESTOR','NÉSTOR','NICOLAS',
    'NICOLÁS','OCTAVIO','OMAR','ORLANDO','OSCAR','ÓSCAR','PABLO','PANCHO','PATRICIO','RAMON',
    'RAMÓN','RAUL','RAÚL','RENE','RENÉ','REYNALDO','RIGOBERTO','ROBERTO','RODOLFO','ROGELIO',
    'ROLANDO','ROMAN','ROMÁN','RUBEN','RUBÉN','SALVADOR','SANTIAGO','SAUL','SAÚL','SERGIO',
    'SILVESTRE','SIMON','SIMÓN','TOMAS','TOMÁS','VICENTE','VICTOR','VÍCTOR','WILFREDO','YOLANDA',
    'ADRIANA','ALICIA','ALMA','AMALIA','ANA','ANGELICA','ANGÉLICA','ARACELI','BEATRIZ','BLANCA',
    'CARMEN','CATALINA','CECILIA','CLAUDIA','CONSUELO','CRISTINA','DIANA','DOLORES','ELENA',
    'ELIZABETH','ELVIRA','EMILIA','ESMERALDA','ESPERANZA','ESTELA','EUGENIA','EVA','FRANCISCA',
    'GABRIELA','GLORIA','GRACIELA','GUADALUPE','HILDA','IRMA','ISABEL','JOSEFINA','JUANA',
    'JULIA','LAURA','LETICIA','LORENA','LOURDES','LUCIA','LUCÍA','LUPE','LUZ','MAGDALENA',
    'MANUELA','MARGARITA','MARIANA','MARISOL','MARTA','MARTHA','MERCEDES','MIRIAM','MONICA',
    'MÓNICA','NORMA','OFELIA','OLGA','PATRICIA','PAULA','PILAR','RAMONA','RAQUEL','REBECA',
    'REYNA','ROCIO','ROCÍO','ROSA','ROSARIO','SANDRA','SILVIA','SOCORRO','SOFIA','SOFÍA',
    'SONIA','SUSANA','TERESA','VERONICA','VERÓNICA','VICTORIA','VIRGINIA','XIMENA','YESENIA',
    'YOLANDA','ALVARO','ÁLVARO','MARIO','ALVARADO','ALVARO','FIDEL','FIDENCIO','GONZALO',
    'CIPRIANO','CRUZ','ADAN','ADÁN','ABEL','ABRAHAM','AGUSTIN','AGUSTÍN','ALBERTO','ALFONSO',
    'ALFREDO','AMADO','AMBROSIO','ANASTASIO','ANIBAL','ANÍBAL','ARNULFO','AURELIO','BALTAZAR',
    'BENITO','BENJAMIN','BENJAMÍN','BERNARDO','CANDELARIO','CARMELO','CIRO','CLEMENTE',
    'CORNELIO','CRISTOBAL','CRISTÓBAL','DARIO','DARÍO','DAVID','DEMETRIO','DIONISIO','DOMINGO',
    'EFRAIN','EFRAÍN','ELEUTERIO','ELISEO','EMILIO','EMMANUEL','EPIFANIO','ERASMO','EULOGIO',
    'EUSEBIO','EZEQUIEL','FABIAN','FABIÁN','FAUSTINO','FAUSTO','FEDERICO','FIDEL','FILIBERTO',
    'FLORENCIO','GABINO','GABRIEL','GASPAR','GENARO','GERMAN','GERMÁN','GERONIMO','GERÓNIMO',
    'GONZALO','GREGORIO','GUADALUPE','HERIBERTO','HERMILO','HILARIO','ILDEFONSO','INDALECIO',
    'INOCENCIO','IRINEO','ISAIAS','ISAÍAS','ISIDORO','ISIDRO','JACINTO','JACOBO','JERONIMO',
    'JERÓNIMO','JOEL','JONAS','JONÁS','JOSUE','JOSUÉ','JOVITA','JOVITO','JULIAN','JULIÁN',
    'LADISLAO','LAZARO','LÁZARO','LEANDRO','LEOBARDO','LEOCADIO','LEONARDO','LEOPOLDO',
    'LIBORIO','LINO','LORENZO','LUCIANO','MACARIO','MARCELINO','MARCELO','MARIANO','MATEO',
    'MATIAS','MATÍAS','MAURICIO','MAXIMILIANO','MAXIMINO','MELCHOR','MELESIO','MELQUIADES',
    'MERCED','MODESTO','NARCISO','NATIVIDAD','NAZARIO','NEMESIO','NICANOR','NORBERTO','ODILON',
    'ODILÓN','ONESIMO','ONÉSIMO','PACIFICO','PACÍFICO','PANFILO','PÁNFILO','PASCUAL','PLACIDO',
    'PLÁCIDO','PONCIANO','PORFIRIO','PRIMO','PROSPERO','PRÓSPERO','QUIRINO','REFUGIO',
    'REMIGIO','RESTITUTO','REYES','ROMAN','ROMÁN','ROMUALDO','ROMULO','RÓMULO','RUBEN',
    'RUFINO','SABAS','SABINO','SAMUEL','SATURNINO','SEBASTIAN','SEBASTIÁN','SEVERINO',
    'SEVERO','SIXTO','SOSTENES','SÓSTENES','TEOFILO','TEÓFILO','TIBURCIO','TIMOTEO','TIRSO',
    'TORIBIO','TRINIDAD','ULISES','URBANO','VALENTIN','VALENTÍN','VALERIO','VENANCIO',
    'VICTORIANO','VIRGILIO','WENCESLAO','ZACARIAS','ZACARÍAS',
  ]);

  const handleDetectHispanic = async () => {
    setBatchRunning(true);
    let updated = 0;
    let pg = 0;
    const pgSize = 1000;
    try {
      while (true) {
        const { data, error } = await supabase
          .from("mailing_records")
          .select("id, first_name, last_name")
          .is("ethnicity_flag", null)
          .not("last_name", "is", null)
          .range(pg * pgSize, (pg + 1) * pgSize - 1);
        if (error) { toast.error("Fetch error: " + error.message); break; }
        if (!data || data.length === 0) break;

        const norm = (s: string) => s.toUpperCase().replace(/,.*$/, "").trim().split(/\s+/)[0];
        const hispanicIds = data
          .filter(r => {
            if (!r.last_name || !r.first_name) return false;
            const ln = norm(r.last_name);
            const fn = norm(r.first_name);
            return HISPANIC_SURNAMES.has(ln) && HISPANIC_FIRST_NAMES.has(fn);
          })
          .map(r => r.id);


        if (hispanicIds.length > 0) {
          for (let i = 0; i < hispanicIds.length; i += 50) {
            const batch = hispanicIds.slice(i, i + 50);
            await supabase.from("mailing_records").update({ ethnicity_flag: "hispanic" }).in("id", batch);
          }
          updated += hispanicIds.length;
        }
        if (data.length < pgSize) break;
        pg++;
      }
      toast.success(`Flagged ${updated} records as Hispanic`);
      fetchRecords();
    } catch (err: any) {
      toast.error("Detection failed: " + err.message);
    } finally {
      setBatchRunning(false);
    }
  };


  const startEditing = (recordId: string, field: string, currentValue: any) => {
    setEditingCell({ id: recordId, field });
    setEditValue(currentValue?.toString() ?? "");
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    const { id, field } = editingCell;
    const colDef = ALL_TABLE_COLUMNS.find(c => c.key === field);
    const dbField = colDef?.dbField || field;
    let dbValue: any = editValue.trim() || null;
    if (colDef?.editType === "number" && dbValue) {
      dbValue = parseFloat(dbValue.replace(/[$,]/g, ""));
      if (isNaN(dbValue)) dbValue = null;
    }
    // Auto-update mailing_status when response is set/cleared
    const updatePayload: Record<string, any> = { [dbField]: dbValue };
    if (field === "response") {
      updatePayload.mailing_status = dbValue ? "responded" : "active";
    }
    const { error } = await supabase.from("mailing_records").update(updatePayload).eq("id", id);
    if (error) {
      toast.error("Save failed: " + error.message);
    } else {
      // Update local state without refetching
      setRecords(prev => prev.map(r => r.id === id ? { ...r, ...updatePayload } : r));
    }
    setEditingCell(null);
  };

  const cancelEdit = () => setEditingCell(null);

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") { e.preventDefault(); saveEdit(); }
    else if (e.key === "Escape") cancelEdit();
    else if (e.key === "Tab") { e.preventDefault(); saveEdit(); }
  };

  const renderCellValue = (record: MailingRecord, col: ColumnDef) => {
    const val = (record as any)[col.dbField || col.key];
    if (col.key === "name") {
      return (
        <div className="flex items-center gap-1.5">
          {record.tm_dropdown ? (
            <Phone className={`h-3 w-3 flex-shrink-0 ${record.tm_dropdown === 'good_call' ? 'text-green-500' : record.tm_dropdown === 'not_interested' || record.tm_dropdown === 'do_not_call' || record.tm_dropdown === 'bad_number' ? 'text-destructive' : 'text-yellow-500'}`} />
          ) : record.phone_number ? (
            <Phone className="h-3 w-3 flex-shrink-0 text-muted-foreground/30" />
          ) : null}
          <span>
            {record.first_name} {record.last_name}
            {record.business_name && <span className="text-[10px] text-muted-foreground block leading-tight">{record.business_name}</span>}
          </span>
        </div>
      );
    }
    if (col.key === "recorded_date") return val ? new Date(val).toLocaleDateString() : "";
    if (col.key === "consideration") return typeof val === "number" ? `$${val.toLocaleString()}` : "";
    if (col.key === "times_mailed") {
      return (
        <Badge variant={record.times_mailed === 0 ? "destructive" : "secondary"} className="text-[10px] px-1.5 py-0">
          {record.times_mailed}
        </Badge>
      );
    }
    if (col.key === "last_mailed_date") return val || "—";
    if (col.key === "mailing_status") {
      return (
        <Badge variant={record.mailing_status === "responded" ? "default" : record.mailing_status === "do_not_mail" ? "destructive" : "outline"} className="text-[10px] px-1.5 py-0">
          {record.mailing_status}
        </Badge>
      );
    }
    if (col.key === "response" && val) {
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0">{val}</Badge>;
    }
    if (col.key === "ethnicity_flag" && val) {
      return <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{val}</Badge>;
    }
    return val ?? "";
  };

  const activeColumns = ALL_TABLE_COLUMNS.filter(c => visibleColumns.has(c.key));

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(column);
      setSortAsc(true);
    }
    setPage(0);
  };

  const SortableHead = ({ column, label }: { column: string; label: string }) => (
    <TableHead className="py-1.5 cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort(column)}>
      <div className="flex items-center gap-1">
        {label}
        {sortColumn === column ? (
          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="cursor-pointer hover:ring-2 ring-primary transition-all" onClick={() => { setMailedFilter("all"); setStatusFilter("all"); setPage(0); }}>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total Records</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 ring-primary transition-all" onClick={() => { setStatusFilter("responded"); setMailedFilter("all"); setPage(0); }}>
          <CardContent className="p-4 flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-bold">{stats.responded.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Responded</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 ring-primary transition-all" onClick={() => { setMailedFilter("0"); setStatusFilter("all"); setPage(0); }}>
          <CardContent className="p-4 flex items-center gap-3">
            <Mail className="h-5 w-5 text-destructive" />
            <div>
              <p className="text-2xl font-bold">{stats.unmailed.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Never Mailed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <MapPin className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-bold">{counties.length}</p>
              <p className="text-xs text-muted-foreground">Counties</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search name, ID, city, address..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9" />
            </div>
            <Select value={countyFilter} onValueChange={(v) => { setCountyFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="County" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Counties</SelectItem>
                {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-[140px] justify-between text-xs" title="Filter by origin state (where the property is recorded)">
                  {stateFilter.length === 0 ? "All Origin States" : stateFilter.length === 1 ? stateFilter[0] : `${stateFilter.length} States`}
                  <MapPin className="h-3 w-3 ml-1 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-2 max-h-[300px] overflow-auto" align="start">
                <div className="space-y-1">
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-7" onClick={() => { setStateFilter([]); setPage(0); }}>
                    All Origin States
                  </Button>
                  {US_STATES.map(({ code, name }) => (
                    <label key={code} className="flex items-center gap-2 px-2 py-1 hover:bg-muted rounded cursor-pointer text-xs">
                      <Checkbox
                        checked={stateFilter.includes(code)}
                        onCheckedChange={(checked) => {
                          setStateFilter(prev => checked ? [...prev, code] : prev.filter(x => x !== code));
                          setPage(0);
                        }}
                      />
                      <span className="font-mono text-muted-foreground">{code}</span>
                      <span>{name}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Select value={docTypeFilter} onValueChange={(v) => { setDocTypeFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Doc Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Doc Types</SelectItem>
                {docTypes.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="responded">Responded</SelectItem>
                <SelectItem value="temporarily_away">Temporarily Away</SelectItem>
                <SelectItem value="do_not_mail">Do Not Mail</SelectItem>
              </SelectContent>
            </Select>
            <Select value={mailedFilter} onValueChange={(v) => { setMailedFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Times Mailed" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mailed</SelectItem>
                <SelectItem value="0">Never Mailed</SelectItem>
                <SelectItem value="1">Mailed Once</SelectItem>
                <SelectItem value="2">Mailed Twice</SelectItem>
                <SelectItem value="2+">Mailed 2+</SelectItem>
              </SelectContent>
            </Select>
            <Select value={letterRoundFilter} onValueChange={(v) => { setLetterRoundFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Letter Round" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rounds</SelectItem>
                <SelectItem value="1">Letter 1</SelectItem>
                <SelectItem value="2">Letter 2</SelectItem>
                <SelectItem value="3">Letter 3</SelectItem>
              </SelectContent>
            </Select>
            <Select value={locationFilter} onValueChange={(v) => { setLocationFilter(v as any); setPage(0); }}>
              <SelectTrigger className="w-[170px]" title="Compare owner's mailing state vs. the state where the document was recorded">
                <SelectValue placeholder="Owner Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Owners</SelectItem>
                <SelectItem value="out_of_state">Out-of-State Owners</SelectItem>
                <SelectItem value="in_state">In-State Owners</SelectItem>
              </SelectContent>
            </Select>
            <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Entity Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="business">Business Only</SelectItem>
                <SelectItem value="individual">Individual Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={responseFilter} onValueChange={(v) => { setResponseFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Response" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Responses</SelectItem>
                <SelectItem value="none">No Response</SelectItem>
                <SelectItem value="has">Has Response</SelectItem>
              </SelectContent>
            </Select>
            <Select value={phoneFilter} onValueChange={(v) => { setPhoneFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Phone" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Phone</SelectItem>
                <SelectItem value="has">Has Phone</SelectItem>
                <SelectItem value="none">No Phone</SelectItem>
              </SelectContent>
            </Select>
            <Select value={calledFilter} onValueChange={(v) => { setCalledFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Called" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Called</SelectItem>
                <SelectItem value="called">Called ✓</SelectItem>
                <SelectItem value="not_called">Not Called</SelectItem>
              </SelectContent>
            </Select>
            <Select value={ageFilter} onValueChange={(v) => { setAgeFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Age" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ages</SelectItem>
                <SelectItem value="65">65+ Years</SelectItem>
                <SelectItem value="55">55+ Years</SelectItem>
                <SelectItem value="45">45+ Years</SelectItem>
                <SelectItem value="35">35+ Years</SelectItem>
              </SelectContent>
            </Select>
            <Select value={ethnicityFilter} onValueChange={(v) => { setEthnicityFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Ethnicity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ethnicity</SelectItem>
                <SelectItem value="hispanic">Hispanic</SelectItem>
                <SelectItem value="none">Not Flagged</SelectItem>
              </SelectContent>
            </Select>
            <Select value={campaignFilter} onValueChange={(v) => { setCampaignFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="Campaign" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">All Campaigns</SelectItem>
                {campaignNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Recorded</span>
              <Input type="date" value={recordedAfter} onChange={(e) => { setRecordedAfter(e.target.value); setPage(0); }} className="w-[140px] h-9" placeholder="From" title="Recorded after (from)" />
              <span className="text-xs text-muted-foreground">to</span>
              <Input type="date" value={recordedBefore} onChange={(e) => { setRecordedBefore(e.target.value); setPage(0); }} className="w-[140px] h-9" placeholder="To" title="Recorded before (to)" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Mailed</span>
              <Input type="date" value={lastMailedAfter} onChange={(e) => { setLastMailedAfter(e.target.value); setPage(0); }} className="w-[140px] h-9" placeholder="From" title="Last mailed after (from)" />
              <span className="text-xs text-muted-foreground">to</span>
              <Input type="date" value={lastMailedBefore} onChange={(e) => { setLastMailedBefore(e.target.value); setPage(0); }} className="w-[140px] h-9" placeholder="On or before" title="Last mailed on or before (inclusive)" />
            </div>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-1" /> Export ({totalCount.toLocaleString()})
            </Button>
            <Button size="sm" variant="secondary" onClick={handleMailExport} disabled={totalCount === 0}>
              <Send className="h-4 w-4 mr-1" /> Mail Export ({totalCount.toLocaleString()})
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Columns3 className="h-4 w-4 mr-1" /> Columns
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-2 max-h-[400px] overflow-auto" align="start">
                <p className="text-xs font-semibold text-muted-foreground mb-2 px-1">Toggle columns</p>
                {ALL_TABLE_COLUMNS.map(col => (
                  <label key={col.key} className="flex items-center gap-2 px-2 py-1 hover:bg-muted rounded cursor-pointer text-xs">
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onCheckedChange={() => toggleColumnVisibility(col.key)}
                    />
                    {col.label}
                  </label>
                ))}
                <div className="border-t mt-2 pt-2 flex gap-1">
                  <Button variant="ghost" size="sm" className="text-xs h-6 flex-1" onClick={() => {
                    const all = new Set(ALL_TABLE_COLUMNS.map(c => c.key));
                    setVisibleColumns(all);
                    localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify([...all]));
                  }}>Show All</Button>
                  <Button variant="ghost" size="sm" className="text-xs h-6 flex-1" onClick={() => {
                    const defaults = new Set(ALL_TABLE_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
                    setVisibleColumns(defaults);
                    localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify([...defaults]));
                  }}>Reset</Button>
                </div>
              </PopoverContent>
            </Popover>
            {(countyFilter !== "all" || stateFilter.length > 0 || statusFilter !== "all" || docTypeFilter !== "all" || mailedFilter !== "all" || entityFilter !== "all" || responseFilter !== "all" || phoneFilter !== "all" || calledFilter !== "all" || ageFilter !== "all" || ethnicityFilter !== "all" || campaignFilter !== "all" || locationFilter !== "all" || recordedBefore || recordedAfter || lastMailedBefore || lastMailedAfter || search) && (
              <Button variant="ghost" size="sm" onClick={() => {
                setSearch(""); setCountyFilter("all"); setStateFilter([]); setStatusFilter("all");
                setDocTypeFilter("all"); setMailedFilter("all"); setEntityFilter("all");
                setResponseFilter("all"); setPhoneFilter("all"); setCalledFilter("all"); setAgeFilter("all"); setEthnicityFilter("all"); setCampaignFilter("all"); setLetterRoundFilter("all"); setLocationFilter("all"); setRecordedBefore(""); setRecordedAfter(""); setLastMailedBefore(""); setLastMailedAfter(""); setPage(0);
              }}>
                Clear Filters
              </Button>
            )}
            {selectedIds.size > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-1" /> Delete ({selectedIds.size})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {selectedIds.size} record(s)?</AlertDialogTitle>
                    <AlertDialogDescription>This action cannot be undone. The selected mailing records will be permanently deleted.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteSelected}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {selectedBusinessRecords.length > 0 && (
              <Button
                variant="default"
                size="sm"
                onClick={handleBatchLookup}
                disabled={batchRunning}
              >
                {batchRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    {batchProgress.done}/{batchProgress.total}
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-1" />
                    Batch Lookup ({selectedBusinessRecords.length})
                  </>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBusinessReview(true)}
            >
              <Building2 className="h-4 w-4 mr-1" /> Review Businesses
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTrusteeReview(true)}
            >
              <Shield className="h-4 w-4 mr-1" /> Review Trustees
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDetectHispanic}
              disabled={batchRunning}
            >
              <Users className="h-4 w-4 mr-1" /> Detect Hispanic
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowReverseDialog(true)}
            >
              <Undo2 className="h-4 w-4 mr-1" /> Reverse Commit
            </Button>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => setShowCommitDialog(true)}
              disabled={totalCount === 0}
            >
              <Send className="h-4 w-4 mr-1" /> Commit to Mail ({totalCount.toLocaleString()})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Commit to Mail Dialog */}
      <AlertDialog open={showCommitDialog} onOpenChange={setShowCommitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Commit {totalCount.toLocaleString()} Records as Mailed</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                This will mark <strong>all {totalCount.toLocaleString()} filtered records</strong> as mailed.
                A mailing history entry will be created for each record with the campaign name and date below. This cannot be easily undone.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Campaign / Mailing Name *</label>
              <Select value={commitCampaign || "__custom__"} onValueChange={(v) => { setCommitCampaign(v === "__custom__" ? "" : v); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a campaign type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Help Access Cash">Help Access Cash</SelectItem>
                  <SelectItem value="Post Card Clean">Post Card Clean</SelectItem>
                  <SelectItem value="Aggressive Insert">Aggressive Insert</SelectItem>
                  <SelectItem value="Follow Up Letter">Follow Up Letter</SelectItem>
                  <SelectItem value="__custom__">Custom...</SelectItem>
                </SelectContent>
              </Select>
              {!["Help Access Cash", "Post Card Clean", "Aggressive Insert", "Follow Up Letter"].includes(commitCampaign) && (
                <Input
                  className="mt-2"
                  placeholder="Enter custom campaign name"
                  value={commitCampaign}
                  onChange={(e) => setCommitCampaign(e.target.value)}
                />
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Mail Date *</label>
              <Input
                type="date"
                value={commitDate}
                onChange={(e) => setCommitDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Limit (optional)</label>
              <Input
                type="number"
                min={1}
                value={commitLimit || ""}
                onChange={(e) => setCommitLimit(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Leave blank to commit all records"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={commitRunning}>Cancel</AlertDialogCancel>
            <Button
              onClick={handleCommitToMail}
              disabled={commitRunning || !commitCampaign.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {commitRunning ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Committing...</>
              ) : (
                <><Send className="h-4 w-4 mr-1" /> Commit {totalCount.toLocaleString()} Records</>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="w-8 py-1.5">
                    <Checkbox
                      checked={selectedIds.size === records.length && records.length > 0}
                      onCheckedChange={(checked) => {
                        setSelectedIds(checked ? new Set(records.map(r => r.id)) : new Set());
                      }}
                    />
                  </TableHead>
                  {activeColumns.map(col => {
                    const sortKey = col.key === "name" ? "last_name" : (col.dbField || col.key);
                    return <SortableHead key={col.key} column={sortKey} label={col.label} />;
                  })}
                  <TableHead className="py-1.5 w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={activeColumns.length + 2} className="text-center py-4 text-sm">Loading...</TableCell></TableRow>
                ) : records.length === 0 ? (
                  <TableRow><TableCell colSpan={activeColumns.length + 2} className="text-center py-4 text-sm text-muted-foreground">No records found</TableCell></TableRow>
                ) : records.map(r => (
                  <TableRow key={r.id} className={`text-xs ${r.tm_dropdown ? 'bg-muted/30' : ''}`}>
                    <TableCell className="py-1">
                      <Checkbox checked={selectedIds.has(r.id)} onCheckedChange={() => toggleSelect(r.id)} />
                    </TableCell>
                    {activeColumns.map(col => {
                      const isEditing = editingCell?.id === r.id && editingCell?.field === col.key;
                      const dbField = col.dbField || col.key;
                      return (
                        <TableCell
                          key={col.key}
                          className={`py-1 ${col.key === "name" ? "font-medium" : ""} ${col.editable ? "cursor-text hover:bg-accent/30" : ""}`}
                          onDoubleClick={() => {
                            if (col.editable) {
                              startEditing(r.id, col.key, (r as any)[dbField]);
                            }
                          }}
                        >
                          {isEditing ? (
                            <Input
                              ref={editInputRef}
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={handleEditKeyDown}
                              className="h-6 text-xs py-0 px-1 w-full min-w-[60px]"
                              type={col.editType === "number" ? "text" : "text"}
                            />
                          ) : (
                            renderCellValue(r, col)
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell className="py-1">
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedRecord(r)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {r.business_name && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setLookupRecord(r)} title="Look up business contact">
                            <Building2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {!r.business_name && (r.first_name || r.last_name) && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMarkAsBusiness(r)} title="Mark as business entity & look up">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" title="Delete record">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                              <AlertDialogDescription>This will permanently delete the record for {r.first_name} {r.last_name}.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteSingle(r.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedRecord} onOpenChange={() => setSelectedRecord(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Record Detail</DialogTitle>
          </DialogHeader>
          {selectedRecord && <MailingRecordDetail
            record={selectedRecord}
            onClose={() => setSelectedRecord(null)}
            onUpdated={() => { fetchRecords(); fetchStats(); }}
            onNavigate={(direction) => {
              const idx = records.findIndex(r => r.id === selectedRecord.id);
              if (direction === "next" && idx < records.length - 1) setSelectedRecord(records[idx + 1]);
              if (direction === "prev" && idx > 0) setSelectedRecord(records[idx - 1]);
            }}
            currentIndex={records.findIndex(r => r.id === selectedRecord.id)}
            totalCount={records.length}
          />}
        </DialogContent>
      </Dialog>

      {/* Business Lookup Dialog */}
      {lookupRecord && (
        <BusinessLookupDialog
          record={lookupRecord}
          open={!!lookupRecord}
          onOpenChange={(open) => { if (!open) setLookupRecord(null); }}
          onApply={handleApplyLookup}
          onDelete={(id) => { setLookupRecord(null); fetchRecords(); fetchStats(); }}
        />
      )}

      {/* Business Entity Review Dialog */}
      <BusinessEntityReview
        open={showBusinessReview}
        onOpenChange={setShowBusinessReview}
        onComplete={() => fetchRecords()}
      />

      {/* Reverse Commit Dialog */}
      <ReverseCommitDialog
        open={showReverseDialog}
        onOpenChange={setShowReverseDialog}
        onReversed={() => { fetchRecords(); fetchStats(); fetchCampaignNames(); }}
      />

      {/* Trustee Review Dialog */}
      <TrusteeReviewDialog
        open={showTrusteeReview}
        onOpenChange={setShowTrusteeReview}
        onComplete={() => fetchRecords()}
      />
    </div>
  );
}
